import React from 'react';

import {
    CheckoutProvider,
    PaymentElement,
    useCheckout,
} from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import axios from 'axios';
import PropTypes from 'prop-types';
import { Trans, useTranslation } from 'react-i18next';

import Button from '@/lib/components/Button';
import styles_psp from '@/lib/components/PspComposer/PspComposer.module.scss';
import WaitingBlock from '@/lib/components/WaitingBlock';
import LyriaPaymentCallbackStatus from '@/lib/constant/lyriaCallbackStatuses';
import { getClientCapabilities } from '@/lib/utils/browserUtils.js';

import styles from './Stripe.module.scss';

const StripeCheckoutForm = React.forwardRef(
    (
        {
            onPaymentResult,
            onPaymentStarted,
            callbackUrl,
            lyriaUrl,
            token,
            paymentRequestId,
            pspId,
            hidePayButton,
            labelPayButton,
            loader,
        },
        ref,
    ) => {
        const { t } = useTranslation();
        const checkout = useCheckout();
        const [isProcessing, setIsProcessing] = React.useState(false);
        const [showWaitingBlock, setShowWaitingBlock] = React.useState(false);
        const [errorMessage, setErrorMessage] = React.useState(null);

        const lockPaymentInFlight = React.useCallback(async () => {
            await axios({
                method: 'post',
                url: `${lyriaUrl}/stripe/process`,
                headers: { 'Content-Type': 'application/json' },
                data: {
                    client_capabilities: getClientCapabilities(),
                    request_token: token,
                    payment_request_id: paymentRequestId,
                    psp: pspId,
                    data: {},
                },
            });
        }, [lyriaUrl, paymentRequestId, pspId, token]);

        const verifyWithLyria = React.useCallback(async () => {
            try {
                const res = await axios({
                    method: 'get',
                    url: callbackUrl,
                    headers: { Accept: 'application/json' },
                });
                const payload = res.data?.data;
                const messageCode = payload?.message?.data?.code || payload?.message?.code;
                // Still PROCESSING after return_url: same path as paymentConfig —
                // send the user to LyriaPay processing page to poll paymentStatus.
                if (
                    messageCode === 'payment_being_processed'
                    && payload?.callback_url
                ) {
                    window.location.href = payload.callback_url;
                    return;
                }
                if (payload?.message?.status === LyriaPaymentCallbackStatus.SUCCESS) {
                    onPaymentResult({ success: true, data: payload });
                } else {
                    onPaymentResult({
                        success: false,
                        data: {
                            code: 'PSPError',
                            error_message: 'Payment could not be verified',
                        },
                    });
                }
            } catch (e) {
                if (e.response?.status === 422 && e.response?.data?.code === 'order_is_cancelled') {
                    onPaymentResult({ success: false, data: { code: 'order_is_cancelled' } });
                } else {
                    onPaymentResult({ success: false, data: { code: 'internal' } });
                }
            }
        }, [callbackUrl, onPaymentResult]);

        const confirmPayment = React.useCallback(async () => {
            if (!checkout?.confirm || isProcessing) {
                return;
            }
            setIsProcessing(true);
            setShowWaitingBlock(true);
            setErrorMessage(null);
            onPaymentStarted?.();

            // Lock payment to PROCESSING before Stripe confirm (same pattern as GlobalPayments).
            // Must happen first: checkout.confirm() may redirect to return_url and never return.
            try {
                await lockPaymentInFlight();
            } catch (error) {
                setIsProcessing(false);
                setShowWaitingBlock(false);
                onPaymentResult({
                    success: false,
                    data: error?.response?.data?.data || { code: 'internal' },
                });
                return;
            }

            const result = await checkout.confirm({ redirect: 'if_required' });
            if (result?.error) {
                // We don't call onPaymentResult here cause user could finish his session in the same tab/same widget
                setErrorMessage(result?.error?.message);
                setShowWaitingBlock(false);
                setIsProcessing(false);
                return;
            }
            await verifyWithLyria();
            setIsProcessing(false);
            setShowWaitingBlock(false);
        }, [
            checkout,
            isProcessing,
            lockPaymentInFlight,
            onPaymentResult,
            onPaymentStarted,
            verifyWithLyria,
        ]);

        const handleSubmit = React.useCallback((event) => {
            event.preventDefault();
            confirmPayment();
        }, [confirmPayment]);

        /* Expose the imperative handle PspComposer uses to trigger payment
           from the shared "Pay" button (see PspComposer.pay()). */
        React.useImperativeHandle(
            ref,
            () => ({
                pay() {
                    confirmPayment();
                    return true;
                },
                canClickOnPay: !!checkout?.confirm && !isProcessing,
            }),
            [checkout, confirmPayment, isProcessing],
        );

        if (!checkout) {
            return loader || <div className={styles_psp.loader} />;
        }

        return (
            <>
                {showWaitingBlock && (
                    <WaitingBlock loader={loader}>
                        <Trans i18nKey="waitingPaymentProcess" />
                    </WaitingBlock>
                )}
                {/* Keep PaymentElement mounted (display:none) so 3DS can reopen it. */}
                <form
                    onSubmit={handleSubmit}
                    className={styles['wz-lyriapay__stripe__form']}
                    style={showWaitingBlock ? { display: 'none' } : undefined}
                >
                    <PaymentElement />
                    {errorMessage && (
                        <div className={styles['wz-lyriapay__stripe__error']}>{errorMessage}</div>
                    )}
                    <div className={styles_psp.common_psp_submit}>
                        <Button
                            hideButton={hidePayButton}
                            disabled={isProcessing || !checkout?.confirm}
                            label={labelPayButton || t('payButton')}
                            type="submit"
                        />
                    </div>
                </form>
            </>
        );
    },
);

StripeCheckoutForm.propTypes = {
    onPaymentResult: PropTypes.func.isRequired,
    onPaymentStarted: PropTypes.func,
    callbackUrl: PropTypes.string.isRequired,
    lyriaUrl: PropTypes.string.isRequired,
    token: PropTypes.string,
    paymentRequestId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    pspId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    hidePayButton: PropTypes.bool,
    labelPayButton: PropTypes.string,
    loader: PropTypes.node,
};

StripeCheckoutForm.displayName = 'StripeCheckoutForm';

const Stripe = React.forwardRef(
    (
        {
            config,
            onPaymentResult,
            onPaymentStarted,
            lyriaUrl,
            token,
            paymentRequestId,
            pspId,
            hidePayButton = false,
            labelPayButton,
            loader,
        },
        ref,
    ) => {
        const {
            publishableKey,
            clientSecret,
            connectedAccountId,
            callbackUrl,
        } = config || {};

        const stripePromise = React.useMemo(() => {
            if (!publishableKey) {
                return null;
            }
            return loadStripe(publishableKey, {
                ...(connectedAccountId ? { stripeAccount: connectedAccountId } : {}),
                developerTools: {
                    assistant: {
                        enabled: window.weezConfig?.environment !== 'production',
                    },
                },
            });
        }, [publishableKey, connectedAccountId]);

        const fetchClientSecret = React.useCallback(
            () => Promise.resolve(clientSecret),
            [clientSecret],
        );

        if (!stripePromise || !clientSecret || !callbackUrl) {
            return loader || null;
        }

        return (
            <div
                id="wz-lyriapay__stripe"
                className={styles['wz-lyriapay__stripe__checkout']}
            >
                <CheckoutProvider
                    stripe={stripePromise}
                    options={{ fetchClientSecret }}
                >
                    <StripeCheckoutForm
                        ref={ref}
                        onPaymentResult={onPaymentResult}
                        onPaymentStarted={onPaymentStarted}
                        callbackUrl={callbackUrl}
                        lyriaUrl={lyriaUrl}
                        token={token}
                        paymentRequestId={paymentRequestId}
                        pspId={pspId}
                        hidePayButton={hidePayButton}
                        labelPayButton={labelPayButton}
                        loader={loader}
                    />
                </CheckoutProvider>
            </div>
        );
    },
);

Stripe.propTypes = {
    config: PropTypes.shape({
        publishableKey: PropTypes.string,
        clientSecret: PropTypes.string,
        connectedAccountId: PropTypes.string,
        callbackUrl: PropTypes.string,
    }).isRequired,
    onPaymentResult: PropTypes.func.isRequired,
    onPaymentStarted: PropTypes.func,
    lyriaUrl: PropTypes.string.isRequired,
    token: PropTypes.string,
    paymentRequestId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    pspId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    hidePayButton: PropTypes.bool,
    labelPayButton: PropTypes.string,
    loader: PropTypes.node,
};

Stripe.displayName = 'Stripe';

export default Stripe;
